'use client'

import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  return (
    <AppErrorBoundary source="ui-select-trigger">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          'bg-theme-bg-secondary border-theme-border-primary backdrop-blur-glass text-theme-text-primary',
          'flex h-11 w-full items-center justify-between rounded-xl px-4 py-2 text-sm font-medium shadow-theme-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 [&>span]:line-clamp-1',
          className
        )}
        {...props}
      >
        {children}
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            className="h-4 w-4 transition-transform duration-150 group-data-[state=open]:rotate-180 text-theme-text-primary opacity-70"
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    </AppErrorBoundary>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => {
  return (
    <AppErrorBoundary source="ui-select-scroll-up">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={cn(
          'flex cursor-default items-center justify-center py-1 text-theme-text-primary opacity-70',
          className
        )}
        {...props}
      >
        <ChevronUp className="h-4 w-4" />
      </SelectPrimitive.ScrollUpButton>
    </AppErrorBoundary>
  )
})
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => {
  return (
    <AppErrorBoundary source="ui-select-scroll-down">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={cn(
          'flex cursor-default items-center justify-center py-1 text-theme-text-primary opacity-70',
          className
        )}
        {...props}
      >
        <ChevronDown className="h-4 w-4" />
      </SelectPrimitive.ScrollDownButton>
    </AppErrorBoundary>
  )
})
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => {
  return (
    <SelectPrimitive.Portal>
      <AppErrorBoundary source="ui-select-content">
        {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
        <SelectPrimitive.Content
          ref={ref}
          className={cn(
            'bg-theme-bg-secondary border-theme-border-primary backdrop-blur-glass text-theme-text-primary',
            'relative z-50 max-h-96 min-w-[10rem] overflow-hidden rounded-xl border shadow-theme-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            position === 'popper' &&
              'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
            className
          )}
          position={position}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              'p-2',
              position === 'popper' &&
                'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </AppErrorBoundary>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => {
  return (
    <AppErrorBoundary source="ui-select-label">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <SelectPrimitive.Label
        ref={ref}
        className={cn('py-1.5 pl-2 pr-2 text-xs uppercase tracking-wide text-theme-text-primary', className)}
        {...props}
      />
    </AppErrorBoundary>
  )
})
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  return (
    <AppErrorBoundary source="ui-select-item">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <SelectPrimitive.Item
        ref={ref}
        className={cn(
          'text-theme-text-primary',
          'relative flex w-full cursor-default select-none items-center rounded-lg px-3 py-2 text-sm outline-none transition-all data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:accent-bg-primary data-[highlighted]:text-theme-text-primary data-[highlighted]:shadow-theme-md',
          className
        )}
        {...props}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <Check className="h-4 w-4 text-theme-text-primary" />
          </SelectPrimitive.ItemIndicator>
        </span>

        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </SelectPrimitive.Item>
    </AppErrorBoundary>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => {
  return (
    <AppErrorBoundary source="ui-select-separator">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <SelectPrimitive.Separator
        ref={ref}
        className={cn('-mx-2 my-2 h-px bg-theme-border-secondary', className)}
        {...props}
      />
    </AppErrorBoundary>
  )
})
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton
}